import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CustomKPITracking = ({ 
  kpis = [], 
  user = null,
  isPremium = false,
  onUpdateKPI,
  className = ""
}) => {
  const [editingKPI, setEditingKPI] = useState(null);
  const [newValue, setNewValue] = useState('');

  const handleUpdateKPI = (kpiId, value) => {
    onUpdateKPI?.(kpiId, value);
    setEditingKPI(null);
    setNewValue('');
  };

  const getProgressColor = (current, target) => {
    const percentage = (current / target) * 100;
    if (percentage >= 100) return "text-success bg-success/10";
    if (percentage >= 75) return "text-primary bg-primary/10";
    if (percentage >= 50) return "text-warning bg-warning/10";
    return "text-error bg-error/10";
  };

  const formatKPIValue = (value, unit) => {
    if (unit === 'percentage') return `${value}%`;
    if (unit === 'currency') return `$${value?.toLocaleString()}`;
    if (unit === 'hours') return `${value}h`;
    return value?.toString();
  };

  const calculateProgress = (current, target) => {
    return Math.min((current / target) * 100, 100);
  };

  if (!isPremium) {
    return (
      <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="Crown" size={32} className="text-warning" />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-foreground">Custom KPI Tracking</h3>
            <p className="text-sm text-muted-foreground">
              Track personalized career metrics and goals with Premium
            </p>
          </div>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex items-center justify-center space-x-2">
              <Icon name="Check" size={12} className="text-success" />
              <span>Custom goal setting</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Icon name="Check" size={12} className="text-success" />
              <span>Progress visualization</span>
            </div>
            <div className="flex items-center justify-center space-x-2">
              <Icon name="Check" size={12} className="text-success" />
              <span>Target comparisons</span>
            </div>
          </div>
          <Button
            variant="default"
            iconName="Crown"
            iconPosition="left"
          >
            Upgrade to Premium
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card border border-border rounded-lg p-6 space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Custom KPIs</h3>
        <div className="flex items-center space-x-2">
          <Icon name="Crown" size={16} className="text-warning" />
          <Button
            variant="outline"
            size="sm"
            iconName="Plus"
            iconPosition="left"
          >
            Add KPI
          </Button>
        </div>
      </div>
      {kpis?.length === 0 ? (
        <div className="text-center py-8 space-y-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <Icon name="BarChart3" size={32} className="text-muted-foreground" />
          </div>
          <div className="space-y-2">
            <h4 className="font-semibold text-foreground">No Custom KPIs</h4>
            <p className="text-sm text-muted-foreground">
              Create personalized metrics to track your career progress
            </p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
          >
            Create Your First KPI
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {kpis?.map((kpi, index) => {
            const progress = calculateProgress(kpi?.currentValue, kpi?.targetValue);
            const isEditing = editingKPI === kpi?.id;
            
            return (
              <div key={kpi?.id || index} className="space-y-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{kpi?.name}</h4>
                    <p className="text-xs text-muted-foreground">{kpi?.description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getProgressColor(kpi?.currentValue, kpi?.targetValue)}`}>
                      {Math.round(progress)}%
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => {
                        setEditingKPI(kpi?.id);
                        setNewValue(kpi?.currentValue?.toString());
                      }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-mono text-foreground">
                      {formatKPIValue(kpi?.currentValue, kpi?.unit)} / {formatKPIValue(kpi?.targetValue, kpi?.unit)}
                    </span>
                  </div>
                  
                  <div className="w-full bg-muted rounded-full h-2">
                    <div
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
                {isEditing && (
                  <div className="flex items-center space-x-2 pt-2 border-t border-border">
                    <input
                      type="number"
                      value={newValue}
                      onChange={(e) => setNewValue(e?.target?.value)}
                      className="flex-1 px-3 py-1 text-sm border border-border rounded bg-background text-foreground"
                      placeholder="Enter new value"
                    />
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Check"
                      onClick={() => handleUpdateKPI(kpi?.id, parseFloat(newValue))}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => {
                        setEditingKPI(null);
                        setNewValue('');
                      }}
                    />
                  </div>
                )}
                {kpi?.lastUpdated && (
                  <div className="text-xs text-muted-foreground">
                    Last updated: {new Date(kpi.lastUpdated)?.toLocaleDateString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomKPITracking;